/**
 * Univerzální graf vývoje ceny podle počtu kusů.
 *
 * Použití:
 *  - Nastavení: tlačítko s ikonou grafu u jednotlivých výpočtů (vazby…) –
 *    křivka se počítá klientsky ze vzorce a aktuálních hodnot formuláře.
 *  - Kalkulátor: tlačítko u pole „Počet kusů" – série cen počítá backend
 *    (POST /api/calculate/curve) z kompletního zadání položky.
 *
 * Graf se vykresluje LÍNĚ – až po kliknutí, v modálu (openPriceChartModal).
 */

const SERIES_COLORS = ['#2563eb', '#059669', '#d97706', '#7c3aed', '#db2777', '#0891b2'];

const fmt = (v) => Math.round(v).toLocaleString('cs-CZ');

/** „Hezký" krok osy (1/2/5 × 10^k) pro daný rozsah a počet kroků. */
function niceStep(range, steps) {
  const raw = range / steps;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  for (const m of [1, 2, 5, 10]) {
    if (raw <= m * mag) return m * mag;
  }
  return 10 * mag;
}

/**
 * Vykreslí graf do kontejneru.
 * @param {HTMLElement} mountEl
 * @param {object} cfg
 *  - series:  [{ label, color?, dash?, points: [{x, y, meta?}] }] – první série je hlavní
 *  - markers: [{ x, label }] – svislé přerušované linky (práh, aktuální náklad…)
 *  - extraLines: [{ x1, y1, x2, y2, label? }] – pomocné přímky (asymptota)
 *  - chips:   [{ label, value, sub?, highlight? }] – vzorkové hodnoty pod grafem
 *  - xLabel:  popisek osy X (default „náklad (ks)")
 */
export function renderPriceChart(mountEl, cfg) {
  const series = (cfg.series || []).filter((s) => s.points && s.points.length);
  if (!series.length) {
    mountEl.innerHTML = '<p class="text-sm text-gray-500 py-6 text-center">Žádná data k zobrazení.</p>';
    return;
  }

  const W = 680, H = 260, L = 64, R = 16, T = 14, B = 32;
  const allPts = series.flatMap((s) => s.points);
  const maxX = Math.max(...allPts.map((p) => p.x));
  const maxY = Math.max(...allPts.map((p) => p.y), 1) * 1.05;
  const x = (n) => L + (n / maxX) * (W - L - R);
  const y = (c) => T + (1 - c / maxY) * (H - T - B);

  // Mřížka Y (3 kroky) + osa X (hezké kroky)
  let grid = '';
  for (let i = 1; i <= 3; i++) {
    const val = (maxY / 1.05) * (i / 3);
    grid += `<line x1="${L}" y1="${y(val)}" x2="${W - R}" y2="${y(val)}" stroke="#e5e7eb" stroke-width="1"/>`
      + `<text x="${L - 6}" y="${y(val) + 3}" text-anchor="end" font-size="10" fill="#9ca3af">${fmt(val)}</text>`;
  }
  const stepX = niceStep(maxX, 5);
  let xTicks = '';
  for (let n = stepX; n <= maxX; n += stepX) {
    xTicks += `<text x="${x(n)}" y="${H - B + 14}" text-anchor="middle" font-size="10" fill="#9ca3af">${fmt(n)}</text>`;
  }

  // Pomocné přímky (asymptoty)
  let extra = '';
  for (const l of cfg.extraLines || []) {
    extra += `<line x1="${x(l.x1)}" y1="${y(l.y1)}" x2="${x(l.x2)}" y2="${y(l.y2)}" stroke="#9ca3af" stroke-width="1" stroke-dasharray="5 4"/>`;
  }

  // Svislé markery (práh, aktuální náklad)
  let markers = '';
  for (const m of cfg.markers || []) {
    if (m.x <= 0 || m.x > maxX) continue;
    markers += `<line x1="${x(m.x)}" y1="${T}" x2="${x(m.x)}" y2="${y(0)}" stroke="#f59e0b" stroke-width="1" stroke-dasharray="4 3"/>`
      + `<text x="${x(m.x) + 4}" y="${T + 10}" font-size="10" fill="#b45309">${escapeHtml(m.label || '')}</text>`;
  }

  // Křivky sérií + tečky s nativním tooltipem na hlavní sérii
  let paths = '';
  series.forEach((s, i) => {
    const color = s.color || SERIES_COLORS[i % SERIES_COLORS.length];
    const d = s.points.map((p, j) => `${j ? 'L' : 'M'}${x(p.x).toFixed(1)},${y(p.y).toFixed(1)}`).join('');
    paths += `<path d="${d}" fill="none" stroke="${color}" stroke-width="2.5" stroke-linejoin="round"${s.dash ? ` stroke-dasharray="${s.dash}"` : ''}/>`;
  });
  const main = series[0];
  const mainColor = main.color || SERIES_COLORS[0];
  const step = Math.max(1, Math.floor(main.points.length / 24));
  let dots = '';
  main.points.forEach((p, j) => {
    if (j % step !== 0 && j !== main.points.length - 1) return;
    const tech = p.meta && p.meta.technologie ? ` [${p.meta.technologie}]` : '';
    dots += `<circle cx="${x(p.x)}" cy="${y(p.y)}" r="3.5" fill="${mainColor}" stroke="#ffffff" stroke-width="1.5">`
      + `<title>${fmt(p.x)} ks → ${fmt(p.y)} Kč (${(p.y / p.x).toFixed(2)} Kč/ks)${tech}</title></circle>`;
  });

  // Legenda jen při více sériích
  const legend = series.length > 1
    ? `<div class="flex flex-wrap gap-x-4 gap-y-1 mb-2 text-xs text-gray-600">${series.map((s, i) => `
        <span class="inline-flex items-center gap-1.5">
          <span class="inline-block w-4 border-t-2" style="border-color:${s.color || SERIES_COLORS[i % SERIES_COLORS.length]};${s.dash ? 'border-top-style:dashed;' : ''}"></span>
          ${escapeHtml(s.label || `Série ${i + 1}`)}
        </span>`).join('')}</div>`
    : '';

  const chips = (cfg.chips || []).map((c) => `
    <span class="inline-flex items-center gap-1 px-2 py-1 ${c.highlight ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-white border-gray-200 text-gray-700'} border rounded-md">
      <strong>${escapeHtml(c.label)}</strong> → ${escapeHtml(c.value)}
      ${c.sub ? `<span class="${c.highlight ? 'text-blue-400' : 'text-gray-400'}">${escapeHtml(c.sub)}</span>` : ''}
    </span>`).join('');

  // Hover prvky: svislý crosshair + zvýrazněný bod na každé sérii
  const hoverDots = series.map((s, i) =>
    `<circle data-hover-dot="${i}" r="4.5" fill="${s.color || SERIES_COLORS[i % SERIES_COLORS.length]}" stroke="#ffffff" stroke-width="2" style="display:none"/>`
  ).join('');

  mountEl.innerHTML = `
    ${legend}
    <div class="relative">
      <svg viewBox="0 0 ${W} ${H}" class="w-full" role="img" aria-label="Graf vývoje ceny podle počtu kusů">
        ${grid}
        <line x1="${L}" y1="${y(0)}" x2="${W - R}" y2="${y(0)}" stroke="#d1d5db" stroke-width="1"/>
        ${xTicks}
        <text x="${W - R}" y="${H - B + 26}" text-anchor="end" font-size="10" fill="#9ca3af">${escapeHtml(cfg.xLabel || 'náklad (ks)')}</text>
        ${extra}
        ${markers}
        ${paths}
        ${dots}
        <line data-hover-line x1="0" y1="${T}" x2="0" y2="${y(0)}" stroke="#6b7280" stroke-width="1" stroke-dasharray="3 3" style="display:none"/>
        ${hoverDots}
      </svg>
      <div data-chart-tooltip class="hidden absolute z-10 pointer-events-none bg-gray-900/90 text-white rounded-md px-2.5 py-1.5 text-xs shadow-lg whitespace-nowrap"></div>
    </div>
    ${chips ? `<div class="flex flex-wrap gap-2 mt-3 text-xs">${chips}</div>` : ''}
  `;

  bindHover(mountEl, { series, W, H, L, R, T, B, maxX, x, y });
}

/**
 * Hover vrstva: crosshair sleduje myš, tooltip ukazuje pro nejbližší náklad
 * celkovou cenu, cenu za kus (a technologii) každé série.
 */
function bindHover(mountEl, ctx) {
  const { series, W, L, R, T, maxX, x, y } = ctx;
  const svg = mountEl.querySelector('svg');
  const wrap = svg.parentElement;
  const tooltip = mountEl.querySelector('[data-chart-tooltip]');
  const hoverLine = svg.querySelector('[data-hover-line]');
  const hoverDots = [...svg.querySelectorAll('[data-hover-dot]')];

  const nearest = (points, n) => {
    let best = points[0];
    for (const p of points) {
      if (Math.abs(p.x - n) < Math.abs(best.x - n)) best = p;
    }
    return best;
  };

  const hide = () => {
    tooltip.classList.add('hidden');
    hoverLine.style.display = 'none';
    hoverDots.forEach((d) => { d.style.display = 'none'; });
  };

  svg.addEventListener('mouseleave', hide);
  svg.addEventListener('mousemove', (evt) => {
    const rect = svg.getBoundingClientRect();
    const vx = (evt.clientX - rect.left) * (W / rect.width);
    if (vx < L || vx > W - R) { hide(); return; }

    const n = ((vx - L) / (W - L - R)) * maxX;
    const main = nearest(series[0].points, n);
    const cx = x(main.x);

    hoverLine.setAttribute('x1', cx);
    hoverLine.setAttribute('x2', cx);
    hoverLine.style.display = '';

    const rows = series.map((s, i) => {
      const p = nearest(s.points, main.x);
      const dot = hoverDots[i];
      if (dot) {
        dot.setAttribute('cx', x(p.x));
        dot.setAttribute('cy', y(p.y));
        dot.style.display = '';
      }
      const tech = p.meta && p.meta.technologie ? ` · ${p.meta.technologie}` : '';
      const label = series.length > 1 ? `<span class="text-gray-300">${escapeHtml(s.label || '')}:</span> ` : '';
      return `<div>${label}<strong>${fmt(p.y)} Kč</strong> <span class="text-gray-300">(${(p.y / p.x).toFixed(2)} Kč/ks${tech})</span></div>`;
    }).join('');

    tooltip.innerHTML = `<div class="font-semibold mb-0.5">${fmt(main.x)} ks</div>${rows}`;
    tooltip.classList.remove('hidden');

    // Pozice tooltip u kurzoru, s překlopením u pravého okraje
    const wrapRect = wrap.getBoundingClientRect();
    const tipW = tooltip.offsetWidth;
    let left = evt.clientX - wrapRect.left + 14;
    if (left + tipW > wrapRect.width - 4) left = evt.clientX - wrapRect.left - tipW - 14;
    let top = evt.clientY - wrapRect.top - tooltip.offsetHeight - 10;
    if (top < 0) top = evt.clientY - wrapRect.top + 14;
    tooltip.style.left = `${Math.max(0, left)}px`;
    tooltip.style.top = `${top}px`;
  });
}

/**
 * Otevře modál s grafem. Data se načítají líně až po otevření.
 * @param {object} opts
 *  - title, subtitle
 *  - load: async () => cfg pro renderPriceChart (throw → zobrazí se chyba)
 */
export function openPriceChartModal({ title, subtitle, load }) {
  const overlay = document.createElement('div');
  overlay.className = 'fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4';
  overlay.innerHTML = `
    <div class="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" role="dialog" aria-modal="true" aria-label="${escapeHtml(title)}">
      <div class="flex items-start justify-between px-5 py-4 border-b border-gray-200">
        <div>
          <h3 class="text-lg font-semibold text-gray-900">${escapeHtml(title)}</h3>
          ${subtitle ? `<p class="text-sm text-gray-500 mt-0.5">${escapeHtml(subtitle)}</p>` : ''}
        </div>
        <button type="button" data-chart-close class="p-1.5 text-gray-400 hover:text-gray-700 rounded-md hover:bg-gray-100" aria-label="Zavřít">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
      <div class="px-5 py-4" data-chart-body>
        <div class="flex items-center justify-center py-12 text-gray-400">
          <svg viewBox="0 0 24 24" fill="none" class="w-8 h-8 animate-spin"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = () => {
    overlay.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e) => { if (e.key === 'Escape') close(); };
  document.addEventListener('keydown', onKey);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay || e.target.closest('[data-chart-close]')) close();
  });

  const body = overlay.querySelector('[data-chart-body]');
  Promise.resolve()
    .then(() => load())
    .then((cfg) => renderPriceChart(body, cfg))
    .catch((err) => {
      console.error('Graf vývoje ceny:', err);
      body.innerHTML = `<div class="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">${escapeHtml(err.message || 'Načtení grafu se nezdařilo')}</div>`;
    });
}

/**
 * Cena podle marginálního ceníku (V2 PUR / V4) – stejný vzorec jako
 * backend/calc/calc-vazba.js cenaMarginalni. Sdílené pro náhled v Nastavení.
 */
export function cenaMarginalni(n, { priprava, kusStart, kusKonec, prah, utlum }) {
  if (n <= prah) return priprava + kusStart * n;
  const nad = n - prah;
  return priprava + kusStart * prah + kusKonec * nad
    + (kusStart - kusKonec) * utlum * (1 - Math.exp(-nad / utlum));
}

/** Body křivky z funkce ceny: {x, y} pro n = 1..maxN. */
export function pointsFromFn(fn, maxN, step = Math.max(1, Math.round(maxN / 200))) {
  const pts = [];
  for (let n = 1; n <= maxN; n += step) pts.push({ x: n, y: fn(n) });
  if (pts[pts.length - 1].x !== maxN) pts.push({ x: maxN, y: fn(maxN) });
  return pts;
}

/** Standardní čipy „n ks → cena (Kč/ks)" pro dané náklady. */
export function chipsFromFn(fn, quantities, highlightN = null) {
  return quantities.map((n) => ({
    label: `${fmt(n)} ks`,
    value: `${fmt(fn(n))} Kč`,
    sub: `(${(fn(n) / n).toFixed(2)} Kč/ks)`,
    highlight: highlightN != null && n === highlightN
  }));
}

function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
